import { OPERATOR_ERROR, isRetryable } from './operatorErrors';
import { asSentence } from './operatorUtils';

/*
 * Which task an answer belongs to, against the one on screen. A write can
 * answer once the operator moved on — to another order, back to the queue,
 * to another flow — and what is on screen is the device's task
 * (`operatorDevice.task`, which `taskEntered` sets), not what a flow's slice
 * still holds. An outcome off screen carries `meta.offScreenTask`, the name
 * its screen gave the task: «ORD-60», «REC-000090».
 */

/**
 * A task's handle, as `taskEntered` keys the screen: `picking:50`, or
 * `picking:` for a flow's queue.
 *
 * @param {string} flow
 * @param {string|number|null} [taskId]
 */
export const taskKeyOf = (flow, taskId) => `${flow}:${taskId ?? ''}`;

/** Whether that task is the one on screen. */
export const isTaskOnScreen = (state, flow, taskId) => state.operatorDevice?.task?.key === taskKeyOf(flow, taskId);

/**
 * The meta of an outcome, read when it arrives: `{offScreenTask: name}` once
 * its task is no longer on screen, `undefined` while it is.
 *
 * @param {object} state
 * @param {string} flow
 * @param {string|number|null} taskId
 * @param {string} name - As the task's screen titles it.
 */
export const offScreenMeta = (state, flow, taskId, name) =>
  isTaskOnScreen(state, flow, taskId) ? undefined : { offScreenTask: name };

/**
 * The band of a task not on screen: what it says, after the task's name.
 *
 * @param {string} name
 * @param {{tone: string, message: string}|null} signal
 */
export const offScreenSignal = (name, signal) => signal && { ...signal, message: `${name}: ${signal.message}` };

/**
 * The band of a write sent on an earlier visit to the task on screen
 * (`meta.earlierVisit`), when its answer asks for a choice or a confirmation:
 * the screen did not take it, so there is no sheet to answer in. `undefined`
 * leaves the default.
 *
 * @param {object} error - As `readOperatorError` reads it.
 */
export const earlierVisitFailure = (error) =>
  [OPERATOR_ERROR.AMBIGUOUS, OPERATOR_ERROR.CONFIRM].includes(error?.kind)
    ? { tone: 'warning', message: 'Una acción anterior no se registró: vuelve a hacerla.' }
    : undefined;

/**
 * Why a write of a task not on screen did not go, named after it. Nothing
 * there can be done from here: a code that is more than one thing has no
 * sheet to choose in, and a write that only its own screen sends again —
 * packing's, which has no key — waits there. A keyed write offers
 * «Reintentar» in its band instead (see `operatorListeners`).
 *
 * @param {string} name - `meta.offScreenTask`.
 * @param {object} error - As `readOperatorError` reads it.
 * @param {string} [failed] - What did not happen: «no se registró», «no se cerró».
 */
export const offScreenFailure = (name, error, failed = 'no se registró') => {
  if (error?.kind === OPERATOR_ERROR.AMBIGUOUS) {
    return offScreenSignal(name, { tone: 'warning', message: `${failed}; ábrela y vuelve a escanear.` });
  }
  if (isRetryable(error)) {
    return offScreenSignal(name, {
      tone: 'warning',
      message: 'sin confirmación del servidor. Ábrela y reintenta antes de seguir.',
    });
  }
  return offScreenSignal(name, { tone: 'error', message: `${failed}. ${asSentence(error?.message)}`.trim() });
};
